import { Space, Tag } from 'antd';
import { Link } from 'react-router-dom';

import { fundEContractStatusColors } from 'apps/investment/constants/fund-econtracts';
import {
  cardTypeOptions,
  identificationStatusColors,
  securityAccountStatusColors,
} from 'apps/investment/constants/user';
import { formatDateTime } from 'utils/formatDate';

import { PieChartOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import { stockAccountStatusColors } from 'apps/investment/constants/stock-accounts';
import { stockBalanceHistoriesTypeColors } from 'apps/investment/constants/stock-balance-histories';
import { DataTableColumnHeader } from 'common/data-table-column-header';
import { FilterFieldConfig } from 'common/dynamic-filter-form';
import OnlineTooltip from 'common/online-tooltip';
import { LabelTooltip } from 'common/ui/label-tooltip';
import UIIconTooltip from 'common/ui/UIIconTooltip';
import VerificationMark from 'common/ui/VerificationMark';
import {
  currencyCell,
  dateCell,
  dateTimeCell,
  linkCell,
} from 'utils/cell-formatter';

export const columnsGenerator = () => [
  {
    accessorKey: 'id',
    header: '',
    cell: ({ getValue }) => linkCell({ getValue, path: '/investment/users' }),
  },

  {
    accessorKey: 'name',
    header: 'Name',
    cell: ({ row, getValue }) => {
      const name = getValue();
      const { email_verified, email, is_online, last_login_at } =
        row.original || {};
      return (
        <Space>
          <OnlineTooltip
            minimal
            isOnline={is_online}
            lastLoginAt={last_login_at}
          />
          <div>
            {name ? <div>{name}</div> : null}
            <VerificationMark value={email} isVerified={email_verified} />
          </div>
        </Space>
      );
    },
  },
  {
    accessorKey: 'phone',
    header: 'Phone',
    cell: ({ row, getValue }) => {
      const phone = getValue();
      const phone_verified = row.original.phone_verified;
      return <VerificationMark value={phone} isVerified={phone_verified} />;
    },
  },
  {
    accessorKey: 'identification',
    header: 'eKYC',
    cell: ({ row, getValue }) => {
      const identification = getValue();
      if (!identification) {
        return '-';
      }

      return (
        <Tag
          color={identificationStatusColors[identification.verification_status]}
        >
          {identification.verification_status_label}
        </Tag>
      );
    },
  },
  {
    accessorKey: 'account_type',
    header: 'Account Type',
    cell: ({ getValue }) => {
      const value = getValue();
      return value == 'business' ? (
        <Tag color={'blue'}>{value}</Tag>
      ) : (
        <Tag color={'green'}>{value}</Tag>
      );
    },
  },
  {
    accessorKey: 'trading_accounts',
    header: 'Trading Accounts',
    cell: ({ row }) => {
      const { fund_accounts, stock_account } = row.original || {};
      return (
        <Space direction="vertical">
          {(fund_accounts || []).map((fund_account: any) => (
            <Tag
              key={fund_account.id}
              color={fund_account.status != 'approved' ? 'orange' : 'blue'}
            >
              {fund_account.fund_manager_name} {fund_account.trading_code}{' '}
              {fund_account.enabled_b2b ? <>(FutureFlex)</> : ''}
              {fund_account.status != 'approved' ? (
                <>({fund_account.status})</>
              ) : (
                ''
              )}
            </Tag>
          ))}
          {stock_account && (
            <Tag color={securityAccountStatusColors[stock_account.status]}>
              NHSV {stock_account.account_number}{' '}
              {stock_account.status !== 1007 ? (
                <>({stock_account.status_label})</>
              ) : (
                ''
              )}
            </Tag>
          )}
        </Space>
      );
    },
  },
  {
    accessorKey: 'is_sign_contract',
    header: 'NHSV eContract',
    cell: ({ row }) => {
      const stock_account = row.original?.stock_account;
      if (stock_account && stock_account.is_sign_contract) {
        return <Tag color={'green'}>Đã ký</Tag>;
      }

      return (
        <Space direction="vertical">
          <Tag color={'yellow'}>Chưa ký</Tag>
          {stock_account && stock_account.is_econtract_link_created ? (
            <Tag color={'green'}>Đã có eContract</Tag>
          ) : (
            <Tag color={'grey'}>Chưa có eContract</Tag>
          )}
        </Space>
      );
    },
  },

  {
    accessorKey: 'created_at',
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title="Created"
        className={'justify-start'}
      />
    ),
    cell: dateTimeCell,
  },
  {
    accessorKey: 'updated_at',
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title="Updated"
        className={'justify-start'}
      />
    ),
    cell: dateTimeCell,
  },
  {
    accessorKey: 'action',
    header: 'Action',
    cell: ({ row }) => {
      const { ekyc_verified, id } = row.original || {};
      return (
        <>
          {ekyc_verified == true && (
            <Link to={`/investment/portfolios?user_id=${id}`}>
              <UIIconTooltip title="View Portfolio" Icon={PieChartOutlined} />
            </Link>
          )}
        </>
      );
    },
  },
];

export const columnsStockAccount = (): ColumnsType => [
  {
    title: '',
    dataIndex: 'id',
    align: 'center',
    render: id => <Link to={`/investment/stock-accounts/${id}`}>View</Link>,
  },
  {
    title: 'Account Number',
    dataIndex: 'account_number',
    key: 'account_number',
  },
  {
    title: 'Nguồn',
    dataIndex: 'source',
  },
  {
    title: 'Account Status',
    dataIndex: 'status',
    align: 'center',
    key: 'status',
    render: (value, record) => {
      return (
        <Tag color={stockAccountStatusColors[record.status]}>
          {record.status_label || record.status}
        </Tag>
      );
    },
  },
  {
    title: 'VSD CK',
    dataIndex: 'is_vsd_approved',
    key: 'is_vsd_approved',
    render: (value, record) => {
      if (value) {
        return <Tag color={'green'}>Đã duyệt</Tag>;
      }

      return <Tag color={'yellow'}>Chưa duyệt</Tag>;
    },
  },
  {
    title: 'Hợp đồng CK',
    dataIndex: 'is_sign_contract',
    key: 'is_sign_contract',
    render: (value, record) => {
      return (
        <Space>
          {record.contract_number ? (
            <Tag color={'green'}>{record.contract_number}</Tag>
          ) : (
            <Tag color={'grey'}>Chưa có hợp đồng</Tag>
          )}
          {value ? (
            <Tag color={'green'}>Đã ký</Tag>
          ) : (
            <Tag color={'yellow'}>Chưa ký</Tag>
          )}
        </Space>
      );
    },
  },

  {
    title: (
      <LabelTooltip
        label={'Hợp đồng CCQ'}
        tooltipContent={'Hợp đồng giao dịch CCQ trên tài khoản chứng khoán'}
      />
    ),
    dataIndex: 'is_esign_contract_approve',
    key: 'is_esign_contract_approve',
    render: (value, record) => {
      if (value) {
        return <Tag color={'green'}>Đã ký</Tag>;
      }

      return <Tag color={'yellow'}>Chưa ký</Tag>;
    },
  },
  {
    title: (
      <LabelTooltip
        label={'Hợp đồng quỹ'}
        tooltipContent={'Các hợp đồng giao dịch với các công ty quỹ'}
      />
    ),
    dataIndex: 'esign_contract_mutual_funds',
    render: (value, record) => {
      return (
        value &&
        value.map(item => (
          <div key={item.fund_manager} style={{ margin: 10 }}>
            <Tag color={fundEContractStatusColors[item.status]}>
              {item.fund_manager.toUpperCase()}: {item.status_label}
            </Tag>
          </div>
        ))
      );
    },
  },
  {
    title: 'Created at',
    dataIndex: 'created_at',
    render: value => (value ? formatDateTime(value) : '-'),
  },
  {
    title: 'Updated at',
    dataIndex: 'updated_at',
    render: value => (value ? formatDateTime(value) : '-'),
  },
];

export const filterConfig: FilterFieldConfig[] = [
  {
    name: 'account_type',
    type: 'select',
    label: 'Account Type',
    placeholder: 'All',
    minWidth: '120px',
    options: [
      { value: 'individual', label: 'Individual' },
      { value: 'business', label: 'Business' },
    ],
    defaultValue: 'all',
  },
  {
    name: 'ekyc_verified',
    type: 'select',
    label: 'Ekyc Verified',
    placeholder: 'All',
    minWidth: '120px',
    options: [
      { value: 'true', label: 'True' },
      { value: 'false', label: 'False' },
    ],
    defaultValue: 'all',
  },
  {
    name: 'id_number__like',
    type: 'text',
    label: 'ID Number',
    placeholder: 'Search ID number...',
    minWidth: '160px',
  },
  {
    name: 'card_type',
    type: 'select',
    label: 'ID Card Type',
    placeholder: 'All',
    minWidth: '160px',
    options: cardTypeOptions.filter(o => o.value !== ''),
    defaultValue: 'all',
  },
];

export const columnsStockBalanceHistories = () => [
  {
    accessorKey: 'created_at',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Created At" />
    ),
    cell: dateTimeCell,
  },
  {
    accessorKey: 'trade_date',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Trade Date" />
    ),
    cell: dateCell,
  },
  {
    accessorKey: 'amount',
    header: 'Amount',
    meta: { className: 'text-right' },
    cell: currencyCell,
  },
  {
    accessorKey: 'balance',
    header: 'Balance',
    meta: { className: 'text-right' },
    cell: currencyCell,
  },

  {
    accessorKey: 'type',
    header: 'Type',
    meta: { className: 'text-center' },
    cell: ({ row, getValue }) => {
      const value = getValue();
      return (
        <Tag color={stockBalanceHistoriesTypeColors[value]}>
          {row.original.type_label}
        </Tag>
      );
    },
  },
  {
    accessorKey: 'note',
    header: 'Note',
  },
];

export const columnsUserIdentificationLogs = () => [
  {
    accessorKey: 'created_at',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Created" />
    ),
    cell: dateTimeCell,
  },
  {
    accessorKey: 'updated_at',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Updated" />
    ),
    cell: dateTimeCell,
  },
  {
    accessorKey: 'identification_version',
    header: 'ID version',
    metaData: {
      className: 'text-center',
    },
  },
  {
    accessoryKey: 'note',
    header: 'Note',
  },
  {
    accessorKey: 'admin_email',
    header: 'Admin Email',
  },
  {
    accessorKey: 'verification_status',
    header: 'Verification Status',
    metaData: {
      className: 'text-center',
    },
    cell: ({ row, getValue }) => {
      const value = getValue();
      return <Tag color={identificationStatusColors[value]}>{value}</Tag>;
    },
  },
];
